import React from "react";
import { Avatar, AvatarProps, Tooltip } from "@mui/material";
import { API_URL } from "../../config";

/* ──────────────────────────────────────────────────────────────────────
   UserAvatar — single source of truth for rendering staff/user avatars
   across the CRM. Replaces every raw `<Avatar>` that previously rendered
   either a hardcoded image or a randomly-colored initials disc.

   Behavior:
     • If the user has a `profile_image` path, render that image
       (objectFit: cover, perfect circle).
     • Otherwise render the user's initials on a NEUTRAL slate disc
       — never a vibrant or rainbow color. Matches the "enterprise
       styling, no colorful avatar backgrounds anymore" rule.

   The component is intentionally tiny — it owns no state and produces
   the same JSX shape an MUI Avatar would, so it can drop into any
   existing layout slot without ripple changes.
   ────────────────────────────────────────────────────────────────────── */

/** Minimal shape any user-like object should expose to UserAvatar. */
export interface AvatarUser {
  profile_image?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  display_name?: string | null;
  username?: string | null;
  /** Optional — used as the alt text and tooltip if `tooltip` is true. */
  name?: string | null;
}

interface UserAvatarProps extends Omit<AvatarProps, "src" | "children"> {
  user: AvatarUser | null | undefined;
  /** Square edge length in pixels. Defaults to 32 (typical row avatar). */
  size?: number;
  /** Wrap in a Tooltip showing the user's resolved display name. */
  tooltip?: boolean;
  /** Override the displayed name (used by tooltip + alt attr). */
  fallbackLabel?: string;
}

/** Compose the public URL for an uploaded profile image. */
function buildImageUrl(path: string | null | undefined): string {
  if (!path) return "";
  // Already an absolute URL (e.g. a CDN or a fully-qualified path).
  if (/^https?:\/\//i.test(path) || path.startsWith("data:")) return path;
  // API_URL ends in "/api"; "/../" reaches Laravel's public storage symlink.
  return `${API_URL}/../${path}`;
}

/** Resolve the best display name for the user. */
function resolveName(u: AvatarUser | null | undefined, fallbackLabel?: string): string {
  if (!u) return fallbackLabel ?? "";
  return (
    u.display_name?.trim() ||
    [u.first_name, u.last_name].filter(Boolean).join(" ").trim() ||
    u.name?.trim() ||
    u.username?.trim() ||
    fallbackLabel ||
    ""
  );
}

/** Compute up-to-2-character initials from a name. */
function resolveInitials(name: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || "?";
}

const UserAvatar: React.FC<UserAvatarProps> = ({
  user,
  size = 32,
  tooltip = false,
  fallbackLabel,
  sx,
  ...rest
}) => {
  const name     = resolveName(user, fallbackLabel);
  const initials = resolveInitials(name);
  const src      = buildImageUrl(user?.profile_image);

  // Neutral fallback palette — slate-on-navy. Identical for every user so
  // no avatar reads as a vibrant pill; only the photo varies. The disc
  // gets a subtle hairline border + soft shadow to read as enterprise.
  const avatar = (
    <Avatar
      src={src || undefined}
      alt={name}
      sx={{
        width: size,
        height: size,
        bgcolor: "#eef0f4",
        color: "#475569",
        fontSize: Math.max(10, Math.round(size * 0.36)),
        fontWeight: 700,
        lineHeight: 1,
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 2px rgba(13,27,42,0.06)",
        // When an image is present, objectFit ensures it fills the
        // circle without distortion (MUI applies this to the inner
        // img element automatically; we set it again defensively).
        "& img": { objectFit: "cover" },
        // Override caller `sx` last so per-call customization (extra
        // ring color, etc.) still works.
        ...(sx as object),
      }}
      {...rest}
    >
      {/* Children only render when src is missing/broken; MUI handles the swap. */}
      {!src && initials}
    </Avatar>
  );

  if (tooltip && name) {
    return (
      <Tooltip title={name} arrow>
        <span style={{ display: "inline-flex" }}>{avatar}</span>
      </Tooltip>
    );
  }
  return avatar;
};

export default UserAvatar;
export { buildImageUrl };
